import { IPropertyChangeDtoGroupTypeEnum } from '@/dtos/models/IPropertyChangeDto';

export const getSyncSuccessTitle = (count: number) =>
  `Updates saved and synced with ${count} ${count === 1 ? 'organization' : 'organizations'}`;

export const SYNC_UPDATES_WARNING_TEXT =
  'This change will apply to all lower-levels organizations where this feature is currently enabled. It will not affect levels where the feature is turned off.';

export const SYNC_UPDATES_STEPS = [
  { key: 'save', value: 'Save updates' },
  { key: 'sync', value: 'Sync updates' },
  { key: 'confirm', value: 'Confirm updates' },
];

export enum SyncUpdatesStep {
  SAVE = 'save',
  SYNC = 'sync',
  CONFIRM = 'confirm',
}

export enum SectionName {
  GENERAL_INFORMATION = 'General Information',
  EMAIL_THEME_CONFIGURATION = 'Email Theme Configuration',
  MOBILE_APP_CUSTOMIZATION = 'Mobile App Customization',
  LINK_MANAGEMENT = 'Link Management',
  HELP = 'Help',
  COMMUNICATION_METHODS = 'Communication Methods',
  EMAIL_TEXT_COMMUNICATIONS = 'Email/Text Communications',
  MULTI_FACTOR_AUTHENTICATION = 'Multi-Factor Authentication',
  STATEMENTS = 'Statements',
  PAPER_STATEMENTS_FEES = 'Paper Statements Fees',
  FUNDING_MODEL = 'Funding Model',
  PAYROLL_CONTRIBUTION = 'Payroll Contribution',
  INDIVIDUAL_CONTRIBUTION = 'Individual Contribution',
  ADMIN_CONTRIBUTIONS = 'Admin Contributions',
  FUNDING_REPLENISHMENT = 'Funding Replenishment',
  PRE_FUND = 'Pre-Fund',
  REIMBURSEMENT_OPTIONS = 'Reimbursement Options',
  AI_ADJUDICATION = 'AI Adjudication',
  ORPHAN_CONFIGURATION = 'Orphan Configuration',
  PLANS_RENEWAL = 'Plans Renewal',
  CARDS = 'Cards',
  METRICS = 'Metrics',
  FILE_MANAGER = 'File Manager',
  TECHNICAL_SETTINGS = 'Technical Settings',
  CLAIMS = 'Claims',
  PENDING_DOCS_ESCALATION = 'Pending Docs Escalation',
  FRAUD_RISK_REVIEW_THRESHOLDS = 'Fraud Risk Review Thresholds',
  BALANCE_ADJUSTMENT = 'Balance Adjustment',
}

export const SKIP_SYNC_LABEL = 'Skip Sync (2/3)';
export const getSyncUpdatesLabel = (count: number) => `Sync ${count} ${count === 1 ? 'Update' : 'Updates'} (2/3)`;

export const CONFIRM_BUTTON_LABEL = 'Confirm (3/3)';
export const BACK_BUTTON_LABEL = 'Back';
export const TABLE_HEADERS = {
  propertyName: 'Property name',
  thisOrg: 'This org',
  lowerLevelOrgs: 'Lower-level orgs',
};

export const SECTION_ORDER: string[] = [
  SectionName.GENERAL_INFORMATION,
  SectionName.EMAIL_THEME_CONFIGURATION,
  SectionName.MOBILE_APP_CUSTOMIZATION,
  SectionName.LINK_MANAGEMENT,
  SectionName.HELP,
  SectionName.MULTI_FACTOR_AUTHENTICATION,
  SectionName.COMMUNICATION_METHODS,
  SectionName.EMAIL_TEXT_COMMUNICATIONS,
  SectionName.STATEMENTS,
  SectionName.PAPER_STATEMENTS_FEES,
  SectionName.FUNDING_MODEL,
  SectionName.PAYROLL_CONTRIBUTION,
  SectionName.INDIVIDUAL_CONTRIBUTION,
  SectionName.ADMIN_CONTRIBUTIONS,
  SectionName.FUNDING_REPLENISHMENT,
  SectionName.PRE_FUND,
  SectionName.REIMBURSEMENT_OPTIONS,
  SectionName.AI_ADJUDICATION,
  SectionName.ORPHAN_CONFIGURATION,
  SectionName.PLANS_RENEWAL,
  SectionName.CARDS,
  SectionName.PENDING_DOCS_ESCALATION,
  SectionName.METRICS,
  SectionName.FILE_MANAGER,
  SectionName.TECHNICAL_SETTINGS,
  SectionName.CLAIMS,
  SectionName.BALANCE_ADJUSTMENT,
];

export const OVERRIDABLE_PROPERTIES_CONFIG: Record<string, {
  groupType?: IPropertyChangeDtoGroupTypeEnum;
  section: string;
  displayName?: string;
  description?: string;
  hasDependencyWarning?: boolean;
  isRoot?: boolean;
}> = {
  sso_only: {
    section: SectionName.GENERAL_INFORMATION,
    displayName: 'Participant portal SSO only',
  },
  admin_sso_only: {
    section: SectionName.GENERAL_INFORMATION,
    displayName: 'Admin portal SSO only',
  },
  allow_employees_to_edit_address: {
    section: SectionName.GENERAL_INFORMATION,
    displayName: 'Allow employees to edit address',
  },
  email_logo_type: {
    section: SectionName.EMAIL_THEME_CONFIGURATION,
    displayName: 'Email logo',
  },
  email_logo_path: {
    section: SectionName.EMAIL_THEME_CONFIGURATION,
    displayName: 'Email Logo Path',
  },
  partner_mobile_app: {
    section: SectionName.MOBILE_APP_CUSTOMIZATION,
    displayName: 'Participant mobile app',
  },
  email_domain: {
    section: SectionName.LINK_MANAGEMENT,
    displayName: 'Domain address',
  },
  privacy_policy_link: {
    section: SectionName.LINK_MANAGEMENT,
    displayName: 'Privacy policy',
  },
  terms_and_conditions_link: {
    section: SectionName.LINK_MANAGEMENT,
    displayName: 'Terms and conditions',
  },
  pricing_consent: {
    section: SectionName.LINK_MANAGEMENT,
    displayName: 'Pricing consent',
  },
  about_us_link: {
    section: SectionName.LINK_MANAGEMENT,
    displayName: 'About us',
  },
  customer_service_is_enabled: {
    section: SectionName.HELP,
    displayName: 'Enable help',
  },
  working_hours: {
    section: SectionName.HELP,
    displayName: 'Working Hours',
  },
  customer_service_number: {
    section: SectionName.HELP,
    displayName: 'Help phone number',
  },
  customer_service_url_name: {
    section: SectionName.HELP,
    displayName: 'Help URL name',
  },
  customer_service_url_link: {
    section: SectionName.HELP,
    displayName: 'Help URL',
  },
  customer_service_email: {
    section: SectionName.HELP,
    displayName: 'Help email',
  },
  admin_chatbot_link: {
    section: SectionName.HELP,
    displayName: 'Admin Chatbot Link',
  },
  participant_chatbot_link: {
    section: SectionName.HELP,
    displayName: 'Participant Chatbot Link',
  },
  require_participant_mfa: {
    groupType: IPropertyChangeDtoGroupTypeEnum.MULTI_FACTOR_AUTHENTICATION,
    section: SectionName.MULTI_FACTOR_AUTHENTICATION,
    displayName: 'Require Participant Multi Factor Authentication',
    isRoot: true,
  },
  require_admin_mfa: {
    groupType: IPropertyChangeDtoGroupTypeEnum.MULTI_FACTOR_AUTHENTICATION,
    section: SectionName.MULTI_FACTOR_AUTHENTICATION,
    displayName: 'Require Admin Multi Factor Authentication',
    isRoot: true,
  },
  account_lockout_time: {
    groupType: IPropertyChangeDtoGroupTypeEnum.MULTI_FACTOR_AUTHENTICATION,
    section: SectionName.MULTI_FACTOR_AUTHENTICATION,
    displayName: 'Account Lockout Time (min)',
    hasDependencyWarning: true,
  },
  communication_channels: {
    section: SectionName.COMMUNICATION_METHODS,
    displayName: 'Communication channels',
  },
  organization_communication_categories: {
    section: SectionName.EMAIL_TEXT_COMMUNICATIONS,
    displayName: 'Communication Categories',
  },
  statement_type: {
    groupType: IPropertyChangeDtoGroupTypeEnum.STATEMENTS,
    section: SectionName.STATEMENTS,
    displayName: 'Create Statements',
    isRoot: true,
  },
  statement_frequency_type: {
    groupType: IPropertyChangeDtoGroupTypeEnum.STATEMENTS,
    section: SectionName.STATEMENTS,
    displayName: 'Frequency',
    hasDependencyWarning: true,
  },
  paper_statement_fees_paid_by: {
    groupType: IPropertyChangeDtoGroupTypeEnum.STATEMENTS,
    section: SectionName.PAPER_STATEMENTS_FEES,
    displayName: 'Paid By',
    hasDependencyWarning: true,
  },
  paper_statement_fees_amount: {
    groupType: IPropertyChangeDtoGroupTypeEnum.PAPER_STATEMENTS_FEES,
    section: SectionName.PAPER_STATEMENTS_FEES,
    displayName: 'Amount',
    isRoot: true,
    hasDependencyWarning: true,
  },
  electronic_funding_mode_id: {
    section: SectionName.FUNDING_MODEL,
    displayName: 'Electronic funding replenishment',
    description: 'This determines who is responsible for paying for claim spend',
  },
  check_funding_model: {
    section: SectionName.FUNDING_MODEL,
    displayName: 'Check writing',
    description: 'This determines which account checks are to be paid from. Nonstandard requires collaboration with Elevate to establish',
  },
  external_payroll_contributions_account_method_pull: {
    groupType: IPropertyChangeDtoGroupTypeEnum.PAYROLL_CONTRIBUTION_PULL,
    section: SectionName.PAYROLL_CONTRIBUTION,
    displayName: 'Method',
    isRoot: true,
  },
  external_payroll_contributions_account_method: {
    section: SectionName.PAYROLL_CONTRIBUTION,
    displayName: 'Method',
    isRoot: true,
  },
  external_payroll_contributions_account_method_push: {
    groupType: IPropertyChangeDtoGroupTypeEnum.PAYROLL_CONTRIBUTION_PUSH,
    section: SectionName.PAYROLL_CONTRIBUTION,
    displayName: 'Method',
    isRoot: true,
  },
  payroll_contributions_hold_period: {
    groupType: IPropertyChangeDtoGroupTypeEnum.PAYROLL_CONTRIBUTION_PULL,
    section: SectionName.PAYROLL_CONTRIBUTION,
    displayName: 'Hold period',
    hasDependencyWarning: true,
  },
  contribution_push_give_up_days: {
    groupType: IPropertyChangeDtoGroupTypeEnum.PAYROLL_CONTRIBUTION_PUSH,
    section: SectionName.PAYROLL_CONTRIBUTION,
    displayName: 'Give up period (days)',
    hasDependencyWarning: true,
  },
  ee_contributions_hold_period: {
    section: SectionName.INDIVIDUAL_CONTRIBUTION,
    displayName: 'EE Contributions Hold Period',
    description: 'The number of days contributions or other credits from an employees personal bank account, such as repayments, will pend on the account before becoming available for spend.',
  },
  allow_manual_contribution: {
    section: SectionName.ADMIN_CONTRIBUTIONS,
    displayName: 'Allow manual contributions',
  },
  allow_scheduled_contribution: {
    section: SectionName.ADMIN_CONTRIBUTIONS,
    displayName: 'Allow scheduled contributions',
  },
  external_replenishment_account_method: {
    section: SectionName.FUNDING_REPLENISHMENT,
    displayName: 'Method',
    isRoot: true,
  },
  replenishment_frequency_type: {
    section: SectionName.FUNDING_REPLENISHMENT,
    displayName: 'Frequency',
  },
  replenishment_day_number: {
    section: SectionName.FUNDING_REPLENISHMENT,
    displayName: 'Day of the week',
  },
  automated_org_lock_enabled: {
    section: SectionName.FUNDING_REPLENISHMENT,
    displayName: 'Automatic Org Lock',
    description: 'Automatically lock claim spend on notional accounts if Pre-fund or Replenishment payments failed.',
  },
  allow_prefund: {
    groupType: IPropertyChangeDtoGroupTypeEnum.PRE_FUND,
    section: SectionName.PRE_FUND,
    displayName: 'Enable Pre-Fund',
    description: 'Enabling allows for either employer or plan level pre-funding',
    isRoot: true,
  },
  prefund_initial_delay_date: {
    groupType: IPropertyChangeDtoGroupTypeEnum.PRE_FUND,
    section: SectionName.PRE_FUND,
    displayName: 'Migration Completion Date',
    description: 'Date all migration enrollments will be loaded into the system. Pre-fund will be calculated on or after the completion date for currently active plans. Future plan years will calculate pre-fund 15 days prior to the plan year start.',
    hasDependencyWarning: true,
  },
  prefund_replenishment_percent: {
    groupType: IPropertyChangeDtoGroupTypeEnum.PRE_FUND,
    section: SectionName.PRE_FUND,
    displayName: 'Percent',
    description: 'Date initial pre-fund will be calculated after migration',
    hasDependencyWarning: true,
  },
  prefund_replenishment_min_value: {
    groupType: IPropertyChangeDtoGroupTypeEnum.PRE_FUND,
    section: SectionName.PRE_FUND,
    displayName: 'Minimum value',
    hasDependencyWarning: true,
  },
  prefund_replenishment_creep_percent: {
    groupType: IPropertyChangeDtoGroupTypeEnum.PRE_FUND,
    section: SectionName.PRE_FUND,
    displayName: 'Election change percentage',
    description: 'Increased elections from the previous pre-fund where additional funds will be collected',
    hasDependencyWarning: true,
  },
  prefund_replenishment_creep_min: {
    groupType: IPropertyChangeDtoGroupTypeEnum.PRE_FUND,
    section: SectionName.PRE_FUND,
    displayName: 'Minimum threshold',
    hasDependencyWarning: true,
  },
  is_reimbursement_allowed: {
    groupType: IPropertyChangeDtoGroupTypeEnum.REIMBURSEMENT_OPTIONS,
    section: SectionName.REIMBURSEMENT_OPTIONS,
    displayName: 'Allow Reimbursement',
    isRoot: true,
  },
  allowed_payment_method_configs: {
    groupType: IPropertyChangeDtoGroupTypeEnum.REIMBURSEMENT_OPTIONS,
    section: SectionName.REIMBURSEMENT_OPTIONS,
    displayName: 'Allowed payment methods',
    hasDependencyWarning: true,
  },

  payment_method_type: {
    groupType: IPropertyChangeDtoGroupTypeEnum.REIMBURSEMENT_OPTIONS,
    section: SectionName.REIMBURSEMENT_OPTIONS,
    displayName: 'Payment method type',
    hasDependencyWarning: true,
  },
  payment_employee_fee: {
    groupType: IPropertyChangeDtoGroupTypeEnum.REIMBURSEMENT_OPTIONS,
    section: SectionName.REIMBURSEMENT_OPTIONS,
    displayName: 'Payment employee fee',
    hasDependencyWarning: true,
  },
  is_bill_pay_allowed: {
    section: SectionName.REIMBURSEMENT_OPTIONS,
    displayName: 'Allow Bill Pay',
  },
  fraud_single_claim_threshold: {
    groupType: IPropertyChangeDtoGroupTypeEnum.FRAUD_THRESHOLD,
    section: SectionName.FRAUD_RISK_REVIEW_THRESHOLDS,
    displayName: 'Single claim',
    isRoot: true,
  },
  fraud_single_claim_threshold_amount: {
    groupType: IPropertyChangeDtoGroupTypeEnum.FRAUD_SINGLE,
    section: SectionName.FRAUD_RISK_REVIEW_THRESHOLDS,
    displayName: 'Single claim amount',
    hasDependencyWarning: true,
  },
  fraud_daily_threshold: {
    groupType: IPropertyChangeDtoGroupTypeEnum.FRAUD_THRESHOLD,
    section: SectionName.FRAUD_RISK_REVIEW_THRESHOLDS,
    displayName: 'Daily',
    isRoot: true,
  },
  fraud_daily_threshold_amount: {
    groupType: IPropertyChangeDtoGroupTypeEnum.FRAUD_DAILY,
    section: SectionName.FRAUD_RISK_REVIEW_THRESHOLDS,
    displayName: 'Daily amount',
    hasDependencyWarning: true,
  },
  fraud_weekly_threshold: {
    groupType: IPropertyChangeDtoGroupTypeEnum.FRAUD_THRESHOLD,
    section: SectionName.FRAUD_RISK_REVIEW_THRESHOLDS,
    displayName: 'Weekly',
    isRoot: true,
  },
  fraud_weekly_threshold_amount: {
    groupType: IPropertyChangeDtoGroupTypeEnum.FRAUD_WEEKLY,
    section: SectionName.FRAUD_RISK_REVIEW_THRESHOLDS,
    displayName: 'Weekly amount',
    hasDependencyWarning: true,
  },
  is_claim_offsets_allowed: {
    groupType: IPropertyChangeDtoGroupTypeEnum.CLAIM_OFFSET,
    section: SectionName.REIMBURSEMENT_OPTIONS,
    displayName: 'Allow Claim Offsets',
    isRoot: true,
  },
  claim_offset_logic: {
    groupType: IPropertyChangeDtoGroupTypeEnum.CLAIM_OFFSET,
    section: SectionName.REIMBURSEMENT_OPTIONS,
    displayName: 'Offset logic',
    hasDependencyWarning: true,
  },
  minimum_payment_reimbursement_amount: {
    section: SectionName.REIMBURSEMENT_OPTIONS,
    displayName: 'Waiting for Funds - Minimum Payment',
  },
  waiting_for_funds_continue_payment: {
    section: SectionName.REIMBURSEMENT_OPTIONS,
    displayName: 'Waiting for Funds - Continue Payment',
  },
  waiting_for_funds_expiration: {
    section: SectionName.REIMBURSEMENT_OPTIONS,
    displayName: 'Days to process claims waiting for funds',
    description: 'The number of days the system will continue to look for funds that can pay claims in the \'waiting for funds\' status',
    hasDependencyWarning: true,
  },
  ai_adjudication: {
    groupType: IPropertyChangeDtoGroupTypeEnum.AI_ADJUDICATION,
    section: SectionName.AI_ADJUDICATION,
    displayName: 'AI Adjudication',
    isRoot: true,
  },
  ai_confidence_level: {
    groupType: IPropertyChangeDtoGroupTypeEnum.AI_ADJUDICATION,
    section: SectionName.AI_ADJUDICATION,
    displayName: 'Claims Processing Confidence Level',
    hasDependencyWarning: true,
  },
  sigis_auto_approve_id: {
    groupType: IPropertyChangeDtoGroupTypeEnum.AI_ADJUDICATION,
    section: SectionName.AI_ADJUDICATION,
    displayName: 'Use SIGIS for Auto Approvals?',
    hasDependencyWarning: true,
  },
  is_organization_orphan: {
    groupType: IPropertyChangeDtoGroupTypeEnum.ORPHAN_CONFIGURATION,
    section: SectionName.ORPHAN_CONFIGURATION,
    displayName: 'Is this employer - orphan?',
    isRoot: true,
  },
  orphan_start_timing_days: {
    groupType: IPropertyChangeDtoGroupTypeEnum.ORPHAN_CONFIGURATION,
    section: SectionName.ORPHAN_CONFIGURATION,
    displayName: 'Start orphan process (days)',
    description: 'Enter number of days after employment termination for orphan process to begin',
    hasDependencyWarning: true,
  },
  orphan_organization_id: {
    groupType: IPropertyChangeDtoGroupTypeEnum.ORPHAN_CONFIGURATION,
    section: SectionName.ORPHAN_CONFIGURATION,
    displayName: 'Specify orphan employer',
    hasDependencyWarning: true,
  },
  allow_renewals: {
    section: SectionName.PLANS_RENEWAL,
    displayName: 'Allow organization self-renew their plans',
    description: 'Partner administrators can renew employer plans independently of this configuration',
  },
  card_offered: {
    groupType: IPropertyChangeDtoGroupTypeEnum.CARDS,
    section: SectionName.CARDS,
    displayName: 'Card',
    isRoot: true,
  },
  allow_dependent_card: {
    groupType: IPropertyChangeDtoGroupTypeEnum.CARDS,
    section: SectionName.CARDS,
    displayName: 'Allow dependent card',
    hasDependencyWarning: true,
  },
  number_of_days_before_plan_start: {
    groupType: IPropertyChangeDtoGroupTypeEnum.CARDS,
    section: SectionName.CARDS,
    displayName: 'Card Production',
    description: 'Enter the number of days prior to plan start that cards should start issuing',
    hasDependencyWarning: true,
  },
  card_processing_start_date: {
    groupType: IPropertyChangeDtoGroupTypeEnum.CARDS,
    section: SectionName.CARDS,
    displayName: 'Card processing start day',
    description: 'When this is set the card production field will not be used',
    hasDependencyWarning: true,
  },
  card_phone_number: {
    groupType: IPropertyChangeDtoGroupTypeEnum.CARDS,
    section: SectionName.CARDS,
    displayName: 'Card phone number',
    hasDependencyWarning: true,
  },
  card_url: {
    groupType: IPropertyChangeDtoGroupTypeEnum.CARDS,
    section: SectionName.CARDS,
    displayName: 'Card URL',
    hasDependencyWarning: true,
  },
  card_employer_name: {
    groupType: IPropertyChangeDtoGroupTypeEnum.CARDS,
    section: SectionName.CARDS,
    displayName: 'Card name',
    hasDependencyWarning: true,
  },
  bin_setup: {
    groupType: IPropertyChangeDtoGroupTypeEnum.CARDS,
    section: SectionName.CARDS,
    displayName: 'BIN setup',
    hasDependencyWarning: true,
  },
  prin_setup: {
    groupType: IPropertyChangeDtoGroupTypeEnum.CARDS,
    section: SectionName.CARDS,
    displayName: 'PRIN setup',
    hasDependencyWarning: true,
  },
  recurring_card_swipes: {
    groupType: IPropertyChangeDtoGroupTypeEnum.CARDS,
    section: SectionName.CARDS,
    displayName: 'Recurring Card Swipes',
    hasDependencyWarning: true,
  },
  push_provisioning: {
    groupType: IPropertyChangeDtoGroupTypeEnum.CARDS,
    section: SectionName.CARDS,
    displayName: 'Push Provisioning',
    description: 'When this is set, the users can add their card to Apple, Google or Samsung Wallet via a mobile app',
    hasDependencyWarning: true,
  },
  documents_needed_reminder: {
    section: SectionName.PENDING_DOCS_ESCALATION,
    displayName: 'Documents needed reminder',
    hasDependencyWarning: true,
  },
  documents_needed_reminder_days: {
    section: SectionName.PENDING_DOCS_ESCALATION,
    displayName: 'Documents needed reminder (days)',
    hasDependencyWarning: true,
  },
  suspend_card: {
    section: SectionName.PENDING_DOCS_ESCALATION,
    displayName: 'Suspend card',
    hasDependencyWarning: true,
  },
  suspend_card_days: {
    section: SectionName.PENDING_DOCS_ESCALATION,
    displayName: 'Suspend card (days)',
    hasDependencyWarning: true,
  },
  suspension_threshold: {
    section: SectionName.PENDING_DOCS_ESCALATION,
    displayName: 'Suspension threshold',
    hasDependencyWarning: true,
  },
  suspension_threshold_amount: {
    section: SectionName.PENDING_DOCS_ESCALATION,
    displayName: 'Suspension threshold amount',
    hasDependencyWarning: true,
  },
  substantiation_rate: {
    section: SectionName.METRICS,
    displayName: 'Substantiation Rate',
  },
  is_file_manager_access_enabled: {
    groupType: IPropertyChangeDtoGroupTypeEnum.FILE_MANAGER,
    section: SectionName.FILE_MANAGER,
    displayName: 'File manager access',
    isRoot: true,
  },
  allowed_file_types: {
    groupType: IPropertyChangeDtoGroupTypeEnum.FILE_MANAGER,
    section: SectionName.FILE_MANAGER,
    displayName: 'Allowed file types',
    hasDependencyWarning: true,
  },
  duplicate_check_file_types: {
    groupType: IPropertyChangeDtoGroupTypeEnum.FILE_MANAGER,
    section: SectionName.FILE_MANAGER,
    displayName: 'Duplicate check file types',
    hasDependencyWarning: true,
  },
  is_file_auto_post_enabled: {
    groupType: IPropertyChangeDtoGroupTypeEnum.FILE_MANAGER,
    section: SectionName.FILE_MANAGER,
    displayName: 'Autopost',
    description: 'Allows files to be processed immediately after submission. Otherwise, files must be manually approved for processing',
    hasDependencyWarning: true,
  },
  file_location: {
    groupType: IPropertyChangeDtoGroupTypeEnum.FILE_MANAGER,
    section: SectionName.FILE_MANAGER,
    displayName: 'Send Files (SFTP)',
    description: 'Delivery of census, enrollment, contribution, and dependent files. Error reports folder will be created within the same location',
    hasDependencyWarning: true,
  },
  session_timeout_warn: {
    section: SectionName.TECHNICAL_SETTINGS,
    displayName: 'Timeout warning (mins)',
    description: 'Enter a number of minutes before showing a session timeout warning popup',
  },
  session_timeout_logout: {
    section: SectionName.TECHNICAL_SETTINGS,
    displayName: 'Timeout logout (mins)',
    description: 'Enter the number of minutes before logout user if warning popup not closed',
  },
  claims_assignment_number: {
    section: SectionName.CLAIMS,
    displayName: 'Claims assignment number',
  },
  hsa_custodian_id: {
    section: SectionName.GENERAL_INFORMATION,
    displayName: 'HSA custodian',
  },
  url: {
    section: SectionName.GENERAL_INFORMATION,
    displayName: 'URL',
  },
  balance_adjustment_hold_period: {
    groupType: IPropertyChangeDtoGroupTypeEnum.BALANCE_ADJUSTMENT_PULL,
    section: SectionName.BALANCE_ADJUSTMENT,
    displayName: 'Hold period',
    hasDependencyWarning: true,
  },
  balance_adjustment_push_give_up_days: {
    groupType: IPropertyChangeDtoGroupTypeEnum.BALANCE_ADJUSTMENT_PUSH,
    section: SectionName.BALANCE_ADJUSTMENT,
    displayName: 'Give up period (days)',
    hasDependencyWarning: true,
  },
};

